import {Crop} from 'react-image-crop';

export type RealSizeType = {
    width: number;
    height: number;
};

export function getImageFromUrl(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve: (image: HTMLImageElement) => void, reject: () => void) => {
        const newImage = new Image();

        newImage.addEventListener('load', () => resolve(newImage), false);
        newImage.addEventListener('error', reject, false);

        newImage.src = imageUrl;
    });
}

type ValidCropType = {
    x: number;
    y: number;
    width: number;
    height: number;
};

type SourceCropRectType = {
    x: number;
    y: number;
    width: number;
    height: number;
};

function makeValidCrop(image: HTMLImageElement, crop: Crop): ValidCropType {
    const {width, height, x, y} = crop;

    if (
        typeof width !== 'number' ||
        typeof height !== 'number' ||
        typeof x !== 'number' ||
        typeof y !== 'number' ||
        width + height === 0
    ) {
        return {
            x: 0,
            y: 0,
            width: image.naturalWidth,
            height: image.naturalHeight,
        };
    }

    return {width, height, x, y};
}

function calculateSourceCropRect(
    image: HTMLImageElement,
    crop: ValidCropType,
    relativeRealSize: RealSizeType
): SourceCropRectType {
    const sourceX = Math.max(0, Math.round(crop.x * relativeRealSize.width));
    const sourceY = Math.max(0, Math.round(crop.y * relativeRealSize.height));
    const requestedSourceWidth = Math.max(1, Math.round(crop.width * relativeRealSize.width));
    const requestedSourceHeight = Math.max(1, Math.round(crop.height * relativeRealSize.height));
    const maxSourceWidth = Math.max(1, image.naturalWidth - sourceX);
    const maxSourceHeight = Math.max(1, image.naturalHeight - sourceY);

    return {
        x: sourceX,
        y: sourceY,
        width: Math.min(requestedSourceWidth, maxSourceWidth),
        height: Math.min(requestedSourceHeight, maxSourceHeight),
    };
}

const DEFAULT_CROP_JPEG_QUALITY = 0.92;
/** Нижняя планка при подгонке под maxOutputBytes: ниже — сильные артефакты JPEG, выше — чаще не уложимся в бюджет байт. */
const DEFAULT_MIN_JPEG_QUALITY = 0.7;
/** Эмпирический показатель: размер JPEG обычно растёт сильнее, чем линейно от quality. */
const JPEG_SIZE_TO_QUALITY_EXPONENT = 0.65;
/**
 * Минимальный вес файла, который бэк принимает для логотипа/фото компании (см. подсказку
 * COMPANY_FORM__GALLERY__HINT__MIN_SIZE в UI). Бюджет по весу оригинала (maxOutputBytes) не должен
 * опускаться ниже этого порога: PNG с плоской графикой (логотипы) часто весит меньше, чем тот же
 * кадр в JPEG на любом качестве, и без этого пола подгонка под бюджет уходит ниже минимума бэка.
 */
const MIN_OUTPUT_BYTES = 10 * 1024;

type GetCropImageOptionsType = {
    maxOutputBytes?: number;
    minJpegQuality?: number;
};

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise<Blob>((resolve: (blob: Blob) => void, reject: () => void): void => {
        canvas.toBlob((blob: Blob | null) => (blob ? resolve(blob) : reject()), 'image/jpeg', quality);
    });
}

async function canvasToJpegBlobUnderMaxBytes(
    canvas: HTMLCanvasElement,
    maxBytes: number,
    minQuality: number
): Promise<Blob> {
    const blobAtFull = await canvasToJpegBlob(canvas, 1);

    if (blobAtFull.size <= maxBytes) {
        return blobAtFull;
    }

    const sizeRatio = maxBytes / blobAtFull.size;
    const estimatedQuality = Math.max(minQuality, Math.min(1, Math.pow(sizeRatio, JPEG_SIZE_TO_QUALITY_EXPONENT)));

    const blobTuned = await canvasToJpegBlob(canvas, estimatedQuality);

    if (blobTuned.size <= maxBytes && blobTuned.size >= MIN_OUTPUT_BYTES) {
        return blobTuned;
    }

    const blobFloor = estimatedQuality === minQuality ? blobTuned : await canvasToJpegBlob(canvas, minQuality);

    // The quality estimate targets the byte budget, not the backend's minimum accepted weight — for
    // flat graphics like logos JPEG size doesn't scale predictably with quality, so it can undershoot
    // that minimum even at the quality floor. Ship full quality then rather than a file the backend
    // will reject as too small.
    return blobFloor.size >= MIN_OUTPUT_BYTES ? blobFloor : blobAtFull;
}

export function getCropImage(
    imageUrl: string,
    crop: Crop,
    relativeRealSize: RealSizeType,
    options?: GetCropImageOptionsType
): Promise<Blob> {
    const minJpegQuality = options?.minJpegQuality ?? DEFAULT_MIN_JPEG_QUALITY;
    const requestedMaxOutputBytes = options?.maxOutputBytes ?? 0;
    const maxOutputBytes = requestedMaxOutputBytes > 0 ? Math.max(requestedMaxOutputBytes, MIN_OUTPUT_BYTES) : 0;

    return getImageFromUrl(imageUrl).then(async (image: HTMLImageElement): Promise<Blob> => {
        const canvas = document.createElement('canvas');
        const {width, height, x, y} = makeValidCrop(image, crop);
        const sourceRect = calculateSourceCropRect(image, {width, height, x, y}, relativeRealSize);

        canvas.width = sourceRect.width;
        canvas.height = sourceRect.height;

        const canvasContext = canvas.getContext('2d');

        if (!canvasContext) {
            throw new Error('[ERROR]: getCropImage - can not get context');
        }

        canvasContext.fillStyle = 'white';
        canvasContext.fillRect(0, 0, canvas.width, canvas.height);

        canvasContext.drawImage(
            image,
            sourceRect.x,
            sourceRect.y,
            sourceRect.width,
            sourceRect.height,
            0,
            0,
            sourceRect.width,
            sourceRect.height
        );

        if (maxOutputBytes > 0) {
            return canvasToJpegBlobUnderMaxBytes(canvas, maxOutputBytes, minJpegQuality);
        }

        return canvasToJpegBlob(canvas, DEFAULT_CROP_JPEG_QUALITY);
    });
}

export function convertFileToImage(file: File): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.addEventListener('loadend', () => {
            const readerResult = reader.result;
            const newImage = new Image();

            if (typeof readerResult === 'string') {
                newImage.src = readerResult;
                newImage.addEventListener('load', () => resolve(newImage), false);
                newImage.addEventListener('error', () => reject(new Error('error loading image')), false);
                return;
            }

            reject(new Error('error loading image'));
        });
        reader.addEventListener('error', () => {
            reject(new Error('error reading file'));
        });
    });
}
